import { natsClient } from './NATSClient';

/**
 * Classification of a `system.ping` liveness-probe failure.
 *
 * - `'dead'`        → positive evidence the socket is dead (a NATS TIMEOUT). This is
 *                     the only signal that should drive a reconnect.
 * - `'inconclusive'`→ the ping told us nothing about socket liveness: either the
 *                     backend does not implement `system.ping` (no-responder / 503)
 *                     or the failure is unrecognized. These must NOT force a reconnect.
 */
export type PingErrorClassification = 'dead' | 'inconclusive';

/**
 * Substrings/codes that identify each ping-failure class. Kept in one place so the
 * matching logic is the single source of truth (design Fix Implementation A.2).
 */
const DEAD_SOCKET_MARKERS = ['timeout'];
const NO_RESPONDER_MARKERS = ['no responders', 'no responder', '503', 'noresponders'];

/** Extract a lowercased, searchable string from an arbitrary thrown value. */
function describePingError(error: unknown): string {
  if (error == null) return '';
  if (typeof error === 'string') return error.toLowerCase();

  const err = error as { code?: unknown; name?: unknown; message?: unknown };
  return [err.code, err.name, err.message]
    .filter((part): part is string | number => part != null && (typeof part === 'string' || typeof part === 'number'))
    .map((part) => String(part).toLowerCase())
    .join(' ');
}

/**
 * Classify a `system.ping` failure as dead-socket evidence or inconclusive.
 *
 * A NATS `TIMEOUT` (`code === 'TIMEOUT'` / "timeout") is treated as `'dead'`: the
 * request never came back, which on a connection whose flags still report
 * "connected" is the dead-socket signal we act on. A no-responder / `503`
 * (`NoResponders`) reply means the backend simply does not answer `system.ping`,
 * which is `'inconclusive'`. Every other (unrecognized) failure is conservatively
 * treated as `'inconclusive'` and logged at warn, so we only ever reconnect on the
 * unambiguous dead-socket signal.
 */
export function classifyPingError(error: unknown): PingErrorClassification {
  const description = describePingError(error);

  // Check no-responder / 503 first: a backend without `system.ping` must never be
  // mistaken for a dead socket (false-positive reconnect — Preservation 3.1).
  if (NO_RESPONDER_MARKERS.some((marker) => description.includes(marker))) {
    return 'inconclusive';
  }

  if (DEAD_SOCKET_MARKERS.some((marker) => description.includes(marker))) {
    return 'dead';
  }

  console.warn(
    '🏥 ConnectionHealthService: Unrecognized ping failure, treating as inconclusive (no reconnect):',
    error,
  );
  return 'inconclusive';
}

/** Default timeout (ms) for the `system.ping` liveness probe. */
const LIVENESS_PROBE_TIMEOUT_MS = 5000;

/**
 * Injectable liveness probe (design Fix Implementation A — "Testability hook").
 *
 * Resolves when the backend answers `system.ping` and rejects when it does not.
 * A rejection is classified by {@link classifyPingError}: a `'dead'` result (NATS
 * `TIMEOUT`) is the dead-socket signal that drives recovery, while `'inconclusive'`
 * (no-responder / 503 / unrecognized) leaves the health check passing.
 *
 * The default issues the real `natsClient.request('system.ping', ..., 5000)`.
 * Tests override this to simulate a stale socket (reject with `TIMEOUT`) or an
 * unsupported ping (reject with 503) without touching the network.
 */
export type LivenessProbe = (timeoutMs: number) => Promise<unknown>;

/** The real liveness probe: a lightweight `system.ping` request over `natsClient`. */
const defaultLivenessProbe: LivenessProbe = (timeoutMs: number) => {
  const pingPayload = {
    action: 'ping',
    timestamp: new Date().toISOString(),
    requestId: `health_check_${Date.now()}`,
  };

  // Resolved lazily at call time so a test spy on `natsClient.request` is honored.
  return natsClient.request('system.ping', pingPayload, timeoutMs);
};

export class ConnectionHealthService {
  private static instance: ConnectionHealthService;
  private healthCheckInterval: ReturnType<typeof setInterval> | null = null;
  private isChecking = false;
  private lastHealthCheck: Date | null = null;
  private consecutiveFailures = 0;
  private maxFailures = 1; // Reduced from 3 - faster recovery

  /**
   * Overridable liveness probe used by {@link performHealthCheck}. Defaults to a
   * real `system.ping` request; tests assign a stub to simulate dead/healthy/503
   * sockets without real network.
   */
  livenessProbe: LivenessProbe = defaultLivenessProbe;

  private constructor() {}

  static getInstance(): ConnectionHealthService {
    if (!ConnectionHealthService.instance) {
      ConnectionHealthService.instance = new ConnectionHealthService();
    }
    return ConnectionHealthService.instance;
  }

  /**
   * Start periodic health checks
   */
  startHealthChecks(intervalMs: number = 15000): void { // Reduced from 30000ms
    if (this.healthCheckInterval) {
      console.log('🏥 ConnectionHealthService: Health checks already running');
      return;
    }

    console.log(`🏥 ConnectionHealthService: Starting health checks every ${intervalMs}ms`);

    this.healthCheckInterval = setInterval(() => {
      this.performHealthCheck();
    }, intervalMs);
  }

  /**
   * Stop health checks
   */
  stopHealthChecks(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
      console.log('🏥 ConnectionHealthService: Health checks stopped');
    }
  }

  /**
   * Perform a health check
   */
  private async performHealthCheck(): Promise<void> {
    if (this.isChecking) {
      console.log('🏥 ConnectionHealthService: Health check already in progress, skipping');
      return;
    }

    this.isChecking = true;
    console.log('🏥 ConnectionHealthService: Performing health check...');

    try {
      // Check NATS connection
      const natsConnected = natsClient.isConnected();
      
      if (!natsConnected) {
        throw new Error('NATS connection lost');
      }

      // Check authentication — only validate if Keycloak is actually loaded on the window
      const keycloak = (window as any).keycloak;
      if (keycloak) {
        const authenticated = keycloak.authenticated;
        const hasToken = !!keycloak.token;
        const tokenExpired = keycloak.isTokenExpired?.();

        if (!authenticated || !hasToken) {
          throw new Error('Authentication lost');
        }

        if (tokenExpired) {
          console.log('🏥 ConnectionHealthService: Token expired, attempting refresh...');
          try {
            await keycloak.updateToken(30);
            console.log('✅ Token refreshed successfully');
          } catch (refreshError) {
            throw new Error(`Token refresh failed: ${refreshError}`);
          }
        }
      } else {
        console.log('🏥 ConnectionHealthService: Keycloak not on window, skipping auth check');
      }

      // Test backend connectivity with a lightweight liveness probe. A dead-socket
      // TIMEOUT is treated as a liveness failure (re-thrown into the outer catch so
      // it counts toward maxFailures and triggers attemptRecovery); a no-responder /
      // 503 / unrecognized failure is inconclusive and the health check still passes
      // (preserves 3.1 for backends without `system.ping`).
      try {
        await this.livenessProbe(LIVENESS_PROBE_TIMEOUT_MS);
        console.log('🏥 ConnectionHealthService: Backend ping successful');
      } catch (pingError) {
        if (classifyPingError(pingError) === 'dead') {
          console.error(
            '🏥 ConnectionHealthService: Liveness probe TIMEOUT — socket appears dead:',
            pingError,
          );
          throw new Error(`Liveness probe failed (dead socket): ${pingError}`);
        }
        console.warn(
          '🏥 ConnectionHealthService: Backend ping inconclusive (treated as pass):',
          pingError,
        );
      }

      // Health check passed
      this.consecutiveFailures = 0;
      this.lastHealthCheck = new Date();
      console.log('✅ ConnectionHealthService: Health check passed');

    } catch (error) {
      this.consecutiveFailures++;
      console.error(`❌ ConnectionHealthService: Health check failed (${this.consecutiveFailures}/${this.maxFailures}):`, error);

      if (this.consecutiveFailures >= this.maxFailures) {
        console.error('🚨 ConnectionHealthService: Multiple consecutive failures detected, attempting recovery...');
        await this.attemptRecovery();
      }

    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Attempt to recover from connection issues
   */
  private async attemptRecovery(): Promise<void> {
    console.log('🔄 ConnectionHealthService: Starting recovery process...');

    try {
      // Reset failure counter
      this.consecutiveFailures = 0;

      // Try to refresh token first
      if ((window as any).keycloak?.authenticated && (window as any).keycloak?.isTokenExpired()) {
        console.log('🔄 ConnectionHealthService: Refreshing expired token...');
        await (window as any).keycloak.updateToken(5);
      }

      // Replace the (possibly zombie) socket with a genuinely new connection.
      //
      // A dead socket after long idle still reports `isConnected() === true`
      // (connection.isClosed() is false and connectionReady is still set), so a
      // `if (!natsClient.isConnected())` guard here would SKIP the reconnect and
      // leave the zombie in place — exactly the bug we are fixing. Instead we
      // always route recovery through `forceReconnect()`, which closes the
      // existing connection and re-establishes it. On success it dispatches both
      // `nats-reconnected` and `connection-recovered`, which `SAPDataProvider`
      // listens for (sets `natsReady`, clears `hasTried`, and refetches). This
      // satisfies Expected Behavior 2.3 and reuses the existing recovery signal
      // rather than introducing a new one (design Fix Implementation A.4).
      console.log('🔄 ConnectionHealthService: Forcing NATS reconnect to replace stale socket...');
      await natsClient.forceReconnect();

      console.log('✅ ConnectionHealthService: Recovery process completed');

    } catch (recoveryError) {
      console.error('❌ ConnectionHealthService: Recovery failed:', recoveryError);
      
      // If recovery fails, we might need to force a page reload or show an error to the user
      console.log('🚨 ConnectionHealthService: Recovery failed, may need manual intervention');
      
      // Dispatch event to notify UI components about persistent connection issues
      window.dispatchEvent(new CustomEvent('connection-recovery-failed', {
        detail: { error: recoveryError }
      }));
    }
  }

  /**
   * Get health status
   */
  getHealthStatus(): {
    isHealthy: boolean;
    lastCheck: Date | null;
    consecutiveFailures: number;
    natsConnected: boolean;
    authenticated: boolean;
  } {
    return {
      isHealthy: this.consecutiveFailures < this.maxFailures,
      lastCheck: this.lastHealthCheck,
      consecutiveFailures: this.consecutiveFailures,
      natsConnected: natsClient.isConnected(),
      authenticated: !!(window as any).keycloak?.authenticated
    };
  }
}

// Export singleton instance
export const connectionHealthService = ConnectionHealthService.getInstance();
